import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { rndProjects, rndStats } from "../../mocks/rnd";
import JsonLd from "../../components/base/JsonLd";

const rndJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "연구개발 R&D - 에너버즈(EnerVerze)",
  "description": "에너버즈는 AI와 에너지 기술의 융합을 통해 차세대 에너지 솔루션을 연구·개발하고 있습니다. 정부 과제와 자체 연구를 병행하며 기술 혁신을 주도합니다.",
  "url": "https://enerverze.com/rnd",
  "inLanguage": "ko-KR",
  "isPartOf": {
    "@type": "WebSite",
    "name": "에너버즈(EnerVerze)",
    "url": "https://enerverze.com/"
  },
  "about": {
    "@type": "Thing",
    "name": "Research & Development",
    "description": "AI Energy Platform, Digital Twin, Smart Grid, Distributed Energy 분야 연구개발"
  }
};

export default function RNDPage() {
  return (
    <div className="min-h-screen bg-enerverze-bg">
      <JsonLd data={rndJsonLd} />
      <Navbar />

      <main>
        {/* Page Header */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20research%20and%20development%20concept%20with%20laboratory%20flasks%20and%20molecular%20structures%20on%20dark%20slate%20background%20neon%20cyan%20and%20green%20accents%20futuristic%20science%20aesthetic%20high%20quality%203D%20render%20minimalist%20design&width=1920&height=600&seq=rnd-hero&orientation=landscape"
              alt="R&D 배경"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-enerverze-bg/60 via-enerverze-bg/80 to-enerverze-bg"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
            <p className="text-sm text-neon-cyan mb-3">Research & Development</p>
            <h1 className="text-4xl md:text-6xl font-bold text-enerverze-text leading-tight mb-4">
              연구개발 <span className="text-neon-cyan">R&D</span>
            </h1>
            <p className="text-base md:text-lg text-enerverze-muted max-w-2xl">
              에너버즈는 AI와 에너지 기술의 융합을 통해 차세대 에너지 솔루션을 연구·개발하고 있습니다. 
              정부 과제와 자체 연구를 병행하며 기술 혁신을 주도합니다.
            </p>
          </div>
        </section>

        {/* R&D Stats */}
        <section className="py-16 md:py-24 border-b border-enerverze-border">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {rndStats.map((stat, idx) => (
                <div
                  key={idx}
                  className="p-6 rounded-2xl bg-enerverze-card border border-enerverze-border text-center"
                >
                  <p className="text-3xl md:text-4xl font-bold text-neon-cyan mb-2">{stat.value}</p>
                  <p className="text-sm text-enerverze-muted">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Research Projects */}
        <section className="py-16 md:py-24">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-enerverze-text mb-2">
                연구 프로젝트
              </h2>
              <p className="text-sm text-enerverze-muted">현재 진행 중인 주요 연구 개발 프로젝트 현황</p>
            </div>

            <div className="space-y-6">
              {rndProjects.map((project, idx) => (
                <div
                  key={idx}
                  className="p-6 md:p-8 rounded-2xl bg-enerverze-card border border-enerverze-border hover:border-neon-cyan transition-all"
                >
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-neon-cyan/10 border border-neon-cyan/30">
                        <i className="ri-flask-line text-3xl text-neon-cyan"></i>
                      </div>
                    </div>
                    <div className="flex-grow">
                      <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 mb-3">
                        <h3 className="text-lg md:text-xl font-semibold text-enerverze-text">
                          {project.title}
                        </h3>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-enerverze-dim/30 text-enerverze-subtle">
                          {project.id}
                        </span>
                      </div>
                      <p className="text-sm text-enerverze-muted leading-relaxed mb-4">
                        {project.desc}
                      </p>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <span className="text-enerverze-subtle">
                          <i className="ri-calendar-line mr-1"></i>
                          {project.period}
                        </span>
                        <span className="text-enerverze-subtle">
                          <i className="ri-building-line mr-1"></i>
                          {project.funder}
                        </span>
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            project.status === '완료'
                              ? 'bg-neon-green/20 text-neon-green'
                              : project.status === '진행중'
                              ? 'bg-neon-cyan/20 text-neon-cyan'
                              : 'bg-enerverze-dim/30 text-enerverze-subtle'
                          }`}
                        >
                          {project.status}
                        </span>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <span
                        className="inline-block px-3 py-1 rounded-lg text-xs font-medium"
                        style={{
                          backgroundColor: project.category === 'AI Energy Platform' ? 'rgba(34,211,238,0.15)' : 'rgba(52,211,153,0.15)',
                          color: project.category === 'AI Energy Platform' ? '#22D3EE' : '#34D399',
                        }}
                      >
                        {project.category}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Research Focus Areas */}
        <section className="py-16 md:py-24 bg-enerverze-card/30 border-y border-enerverze-border">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-enerverze-text mb-2">
                핵심 연구 <span className="text-neon-cyan">분야</span>
              </h2>
              <p className="text-sm text-enerverze-muted">에너버즈 R&D의 4대 핵심 연구 방향</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  title: 'AI Energy Platform',
                  desc: '머신러닝과 딥러닝을 활용한 에너지 수요 예측 및 최적화 알고리즘 연구',
                  icon: 'ri-brain-line',
                },
                {
                  title: 'Digital Twin',
                  desc: '물리적 에너지 인프라의 실시간 디지털 복제 및 시뮬레이션 기술 개발',
                  icon: 'ri-box-3-line',
                },
                {
                  title: 'Smart Grid',
                  desc: '지능형 전력망 운영을 위한 실시간 모니터링 및 제어 기술 연구',
                  icon: 'ri-flashlight-line',
                },
                {
                  title: 'Distributed Energy',
                  desc: '분산형 재생에너지의 효율적 통합 및 네트워크 제어 기술 개발',
                  icon: 'ri-sun-line',
                },
              ].map((area, idx) => (
                <div
                  key={idx}
                  className="p-6 rounded-2xl bg-enerverze-card border border-enerverze-border hover:border-neon-cyan transition-all group"
                >
                  <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-neon-cyan/10 border border-neon-cyan/30 mb-5 group-hover:bg-neon-cyan/20 transition-colors">
                    <i className={`${area.icon} text-2xl text-neon-cyan`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-enerverze-text mb-3">{area.title}</h3>
                  <p className="text-sm text-enerverze-muted leading-relaxed">{area.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}